import { Box, Icon, Modal, Tag, Text } from '@nimbus-ds/components';
import { StarIcon } from '@nimbus-ds/icons';
import { InteractiveList } from '@nimbus-ds/patterns';
import { Trans } from 'react-i18next';
import { LocationResponseDto } from '@tiendanube/common';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';

interface SelectDestinationLocationModalProps {
  locationId: string;
  locationsList: LocationResponseDto[];
  locationName: string;
  isConfirmDisabled: boolean;
  onConfirm: () => void;
  onSelectLocation: (locationId: string) => void;
  onClose: () => void;
}

function SelectDestinationLocationModal({
  locationId,
  locationsList,
  locationName,
  isConfirmDisabled,
  onConfirm,
  onSelectLocation,
  onClose,
}: Readonly<SelectDestinationLocationModalProps>) {
  const t = useTranslationShipping();

  return (
    <>
      <Modal.Header
        title={t('locations.deleteSteps.selectDestinationLocation.title', {
          name: locationName,
        })}
      />
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="4" marginBottom="4">
          <Text>
            <Trans
              t={t}
              i18nKey="locations.deleteSteps.selectDestinationLocation.description"
              values={{ name: locationName }}
              components={{ bold: <strong /> }}
            />
          </Text>
          <Text>
            {t('locations.deleteSteps.selectDestinationLocation.description2', {
              name: locationName,
            })}
          </Text>
          <div style={{ marginLeft: '-16px', marginRight: '-16px' }}>
            <InteractiveList>
              {locationsList
                .filter((location) => location.id !== locationId)
                .map((location) => (
                  <InteractiveList.RadioItem
                    key={location.id}
                    radio={{
                      name: 'destination-location',
                      value: location.id,
                      onChange: () => onSelectLocation(location.id),
                    }}
                    title={location.name}
                    showTitle={false}
                  >
                    <Stack spacing="tight" align="flex-start">
                      <Text color="neutral-textHigh">{location.name}</Text>
                      {location.main && (
                        <Tag appearance="primary">
                          <Icon source={<StarIcon />} />
                          {t('locations.card.label')}
                        </Tag>
                      )}
                    </Stack>
                  </InteractiveList.RadioItem>
                ))}
            </InteractiveList>
          </div>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Stack spacing="base" justify="flex-end">
          <CancelAndConfirmButtons
            appearance="danger"
            onCancel={onClose}
            onConfirm={onConfirm}
            confirmText={t(
              'locations.deleteSteps.selectDestinationLocation.confirm',
            )}
            isConfirmDisabled={isConfirmDisabled}
          />
        </Stack>
      </Modal.Footer>
    </>
  );
}

export default SelectDestinationLocationModal;
